// @todo: Migrate to @wandb/components. For more details see:
// https://www.notion.so/wandbai/Migrating-components-207e2f5c7ef380129201f72a5770a8db
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button} from '@wandb/weave/components';
import React, {Dispatch, SetStateAction} from 'react';

import {
  Maybe,
  OrganizationSubscriptionInfoQuery,
  OrganizationSubscriptionType,
  Plan,
} from '../../../../../../generated/graphql';
import {
  getPlanPrivileges,
  HasSubsWithPlanBillingInterval,
  HasSubsWithPlanType,
  isPrimaryStripePlanInfoWithStripePrice,
  Org,
  orgPrimarySubBillingInterval,
  PrimarySubscriptionWithStripePlanInfo,
} from '../../../../../../util/accounts/pricing';
import {NUM_SECONDS_IN_HOUR, secondsToHours} from '../../../../../../util/date';
import {contactSalesPricing} from '../../../../../../util/urls';
import {PlanName} from '../../../../util';
import * as S from '../../CurrentPlanSection.styles';
import {AccountUsage} from '../../PersonalEntityTotalUsageSection';
import {PlanCardProps} from './PlanCard';
import {
  getAcademicPlanCardProps,
  getEnterprisePlanCardProps,
  getEnterpriseTrialCardProps,
  LOCAL_LICENSE_CARD,
  ManageUserButton,
  PERSONAL_PLAN_CARD,
} from './PlanCardTemplates';

export const UPGRADE_SELF_SERVICE_MODAL_QUERY_PARAM =
  'openUpgradeSelfServicePlanModal';
export const UPGRADE_TEAMS_MODAL_QUERY_PARAM = 'openUpgradeTeamsPlanModal';

export enum ActiveModalType {
  UpgradeSelfServicePlanModal = 'upgrade self service plan mdoal',
  UpgradeTeamsPlanModal = 'upgrade teams plan modal',
}

const NoticeContainer = ({
  accountUsage,
  billingInterval,
}: {
  accountUsage?: AccountUsage;
  billingInterval: string;
}) => {
  if (accountUsage == null) {
    return null;
  }
  const {trackedHourUsageSeconds, trackedHourLimitHours} = accountUsage;
  if (trackedHourUsageSeconds == null || trackedHourLimitHours == null) {
    return null;
  }
  const usageLimitSeconds = trackedHourLimitHours * NUM_SECONDS_IN_HOUR;
  const usagePercentage = trackedHourUsageSeconds / usageLimitSeconds;

  let displayHeader = '';
  const intervalAdverb = billingInterval === 'month' ? 'monthly' : 'annual';
  if (usagePercentage < 0.85) {
    return null;
  }
  if (usagePercentage < 1) {
    const remainingPercentage = (1 - usagePercentage) * 100;
    displayHeader = `You only have ${Math.ceil(
      remainingPercentage
    )}% of your ${intervalAdverb} tracked hours left.`;
  } else {
    displayHeader = `You have used ${secondsToHours(
      trackedHourUsageSeconds
    )} of your ${trackedHourLimitHours} ${intervalAdverb} tracked hours.`;
  }
  return (
    <S.NoticeContainer>
      <S.NoticeContainerBoldText>{displayHeader}</S.NoticeContainerBoldText>
      <S.NoticeContainerText>
        Additional hours are billed at $1 / hour
      </S.NoticeContainerText>
    </S.NoticeContainer>
  );
};

export function getTeamsPlanCard(
  setActiveModal: Dispatch<SetStateAction<ActiveModalType | null>>,
  organization: OrganizationSubscriptionInfoQuery['organization'],
  primarySubscription: PrimarySubscriptionWithStripePlanInfo,
  accountUsage?: AccountUsage
): PlanCardProps | null {
  if (!isPrimaryStripePlanInfoWithStripePrice(primarySubscription.plan)) {
    return null;
  }

  const planName =
    primarySubscription.plan.name === PlanName.MonthlyTeams2023
      ? 'Teams monthly plan'
      : primarySubscription.plan.name === PlanName.YearlyTeams2023
        ? 'Teams annual plan'
        : primarySubscription.plan.displayName;

  const chargePerUser: number = primarySubscription.plan.stripePrice.amount;
  const interval = primarySubscription.plan.billingInterval;
  const maxComputeHours =
    primarySubscription.plan.defaultPrivileges.compute_hours;

  const subtitle = (
    <S.SubtitleWrapper>
      <S.SubtitleBoldText>
        {chargePerUser.toLocaleString('en-US', {
          style: 'currency',
          currency: 'USD',
        })}
      </S.SubtitleBoldText>{' '}
      per user per {interval}
    </S.SubtitleWrapper>
  );
  const descriptionList = [
    <>{primarySubscription.seats} out of 10 user seats</>,
    `Up to ${maxComputeHours.toLocaleString()} tracked hours / ${interval}`,
    'Email and chat support',
  ];

  const description = (
    <>
      <S.DescriptionList>
        {descriptionList.map((text, i) => (
          <S.DescriptionListItem key={i}>{text}</S.DescriptionListItem>
        ))}
      </S.DescriptionList>
      <NoticeContainer accountUsage={accountUsage} billingInterval={interval} />
    </>
  );

  const yearlyNextPlanCTA = undefined;

  const monthlyNextPlanCTA = (
    <Button
      size="large"
      onClick={() => {
        window.analytics?.track('Add Tracked Hours Clicked', {
          location: 'primary plan card add tracked hours button',
          organizationName: organization?.name ?? '',
        });
        setActiveModal(ActiveModalType.UpgradeTeamsPlanModal);
      }}>
      Upgrade to annual and save
    </Button>
  );

  const nextPlanCTA =
    primarySubscription.plan.name === PlanName.MonthlyTeams2023
      ? monthlyNextPlanCTA
      : yearlyNextPlanCTA;

  return {
    planName,
    subtitle,
    description,
    nextPlanCTA,
  };
}

export function convertOrganizationToOrg(
  organization?: OrganizationSubscriptionInfoQuery['organization']
): Org | null {
  if (organization == null) {
    return null;
  }

  return {
    id: organization.id,
    name: organization.name,
    usedSeats: organization.usedSeats,
    teams: organization.teams,
    subscriptions: organization.subscriptions,
    flags: organization.flags,
  };
}

export function getLegacyPlanData(
  setActiveModal: Dispatch<SetStateAction<ActiveModalType | null>>,
  organization:
    | (Pick<PlanCardOrganization, 'name'> &
        HasSubsWithPlanType &
        HasSubsWithPlanBillingInterval)
    | null,
  primarySubscription: Pick<
    PlanCardPrimarySubscription,
    'subscriptionType' | 'expiresAt' | 'seats' | 'plan' | 'nextPlans'
  >
): PlanCardProps | null {
  const organizationCurrentPlanData: PlanCardProps | null =
    getCurrentPlanDataForOrganization(organization, primarySubscription);
  // something went wrong with getting props for PlanCard from organization data
  if (organizationCurrentPlanData == null) {
    return null; // error
  }

  const organizationNextPlanCTA = getNextPlanCTAForOrganization(
    organization?.name ?? '',
    primarySubscription,
    setActiveModal
  );

  return {
    ...organizationCurrentPlanData,
    nextPlanCTA: organizationNextPlanCTA,
  };
}

export function getCurrentPlanDataForPersonal() {
  return PERSONAL_PLAN_CARD;
}

export function getSelfServicePrimaryPlanCard(
  organization:
    | (Pick<PlanCardOrganization, 'name'> &
        HasSubsWithPlanType &
        HasSubsWithPlanBillingInterval)
    | null,
  primarySubscription: Pick<
    PlanCardPrimarySubscription,
    'subscriptionType' | 'expiresAt' | 'seats' | 'plan'
  >
): PlanCardProps {
  const totalCharge =
    (primarySubscription.seats ?? 0) *
    (primarySubscription.plan?.stripePrice?.amount ?? 0);

  const billingInterval =
    organization != null ? orgPrimarySubBillingInterval(organization) : null;

  const subtitle =
    primarySubscription.plan?.stripePrice == null ? (
      <S.SubtitleWrapper>Free</S.SubtitleWrapper>
    ) : (
      <S.SubtitleWrapper>
        {primarySubscription.seats} users{' '}
        <S.SubtitleBoldText>${totalCharge.toLocaleString()}</S.SubtitleBoldText>
        /{billingInterval}
      </S.SubtitleWrapper>
    );

  const privileges = getPlanPrivileges(primarySubscription.plan);

  const descriptionList = [
    `1 team up to ${primarySubscription.plan.maxSeats} seats or users`,
    `${
      privileges.computeHours !== 0
        ? `Up to ${privileges.computeHours.toLocaleString()} cumulative tracked hours`
        : `Unlimited tracked hour`
    }`,
    `Email and chat with support`,
    `${privileges.storageLimitGB.toLocaleString()} GB Storage`,
  ];

  const planName = getPlanCardDisplayName(
    primarySubscription.plan?.displayName ?? ''
  );
  return {
    planName,
    subtitle,
    description: (
      <S.DescriptionList>
        {descriptionList.map((text, i) => (
          <S.DescriptionListItem key={i}>{text}</S.DescriptionListItem>
        ))}
      </S.DescriptionList>
    ),
    // nextPlanCTA is fetched separately with getNextPlan function
    currentPlanCTA: (
      <ManageUserButton organizationName={organization?.name ?? ''} />
    ),
  };
}

export function getPlanCardDisplayName(planName: string) {
  // this is a hot fix for showing the correct plan name in the plan card
  // plan.name could be either displayName or plan.name if displayName is absent
  // planName could be either displayName or plan.name
  const lowerPlanName = planName.toLowerCase();
  switch (lowerPlanName) {
    case 'free trial':
    case 'basic':
      return 'Starter Free Trial';
    case 'startup_yearly':
    case 'startup_monthly':
    case 'archived-startup-monthly':
      return 'Startup';
    case 'teams_monthly':
    case 'teams_yearly':
    case 'archived-teams-monthly-1':
    case 'archived-teams-monthly-2':
    case 'archived-teams-yearly':
      return 'Teams';
    case 'standard_yearly':
    case 'standard_monthly':
    case 'starter yearly':
    case 'starter monthly':
      return 'Standard';
    case 'starter_tier_1_yearly':
    case 'starter_tier_1_monthly':
    case 'starter tier 1 yearly':
    case 'starter tier 1 monthly':
      return 'Starter - Tier 1';
    case 'starter_tier_2_yearly':
    case 'starter_tier_2_monthly':
    case 'starter tier 2 yearly':
    case 'starter tier 2 monthly':
      return 'Starter - Tier 2';
    case 'starter_tier_3_yearly':
    case 'starter_tier_3_monthly':
    case 'starter tier 3 yearly':
    case 'starter tier 3 monthly':
      return 'Starter - Tier 3';
    default:
      return planName;
  }
}

type PlanCardOrganization = NonNullable<
  OrganizationSubscriptionInfoQuery['organization']
>;

export type PlanCardPrimarySubscription = Pick<
  NonNullable<PlanCardOrganization['subscriptions']>[number],
  'subscriptionType' | 'seats' | 'plan' | 'nextPlans' | 'expiresAt'
>;

export function getEnterprisePlanCard(
  organization: Pick<PlanCardOrganization, 'name'> | null,
  primarySubscription: Pick<
    PlanCardPrimarySubscription,
    'subscriptionType' | 'expiresAt'
  >
) {
  if (
    primarySubscription.subscriptionType ===
      OrganizationSubscriptionType.Academic ||
    primarySubscription.subscriptionType ===
      OrganizationSubscriptionType.AcademicTrial
  ) {
    return getAcademicPlanCardProps(organization?.name ?? '');
  }

  if (
    primarySubscription.subscriptionType === OrganizationSubscriptionType.Local
  ) {
    return LOCAL_LICENSE_CARD;
  }

  if (
    primarySubscription.subscriptionType ===
    OrganizationSubscriptionType.ManualTrial
  ) {
    return getEnterpriseTrialCardProps(
      organization?.name ?? '',
      primarySubscription.expiresAt
    );
  }

  return getEnterprisePlanCardProps(organization?.name ?? '');
}

export type PrimarySubscription = {
  plan: Plan;
  subscriptionType: OrganizationSubscriptionType;
  seats: number;
  expiresAt?: Maybe<string>;
  nextPlans: Plan[];
  __typename: 'OrganizationSubscription';
};

export function getCurrentPlanDataForOrganization(
  organization:
    | (Pick<PlanCardOrganization, 'name'> &
        HasSubsWithPlanType &
        HasSubsWithPlanBillingInterval)
    | null,
  primarySubscription?: Pick<
    PlanCardPrimarySubscription,
    'subscriptionType' | 'expiresAt' | 'seats' | 'plan'
  >
): PlanCardProps | null {
  if (primarySubscription == null) {
    return null;
  }

  if (primarySubscription?.plan?.name === PlanName.Enterprise) {
    const enterprisePlanCardData = getEnterprisePlanCard(
      organization,
      primarySubscription
    );
    return enterprisePlanCardData;
  }

  const selfServicePrimaryPlanCardData = getSelfServicePrimaryPlanCard(
    organization,
    primarySubscription
  );

  return selfServicePrimaryPlanCardData;
}

export function getNextPlanCTAForOrganization(
  organizationName: string,
  primarySubscription: Pick<
    PlanCardPrimarySubscription,
    'subscriptionType' | 'expiresAt' | 'seats' | 'plan' | 'nextPlans'
  >,
  setActiveModal: Dispatch<SetStateAction<ActiveModalType | null>>
): JSX.Element | undefined {
  if (
    primarySubscription.nextPlans == null ||
    primarySubscription.nextPlans.length === 0
  ) {
    return undefined;
  }

  if (
    primarySubscription.nextPlans.length === 1 &&
    primarySubscription.nextPlans[0].name === 'enterprise'
  ) {
    return (
      <S.PrimaryCTAButton>
        <S.PrimaryCTATargetBlank
          onClick={() =>
            window.analytics?.track('Upgrade to Enterprise Clicked', {
              location: 'primary plan card upgrade to enterprise button',
              organizationName,
            })
          }
          href={contactSalesPricing()}>
          Upgrade to enterprise
        </S.PrimaryCTATargetBlank>
      </S.PrimaryCTAButton>
    );
  }

  if (
    primarySubscription.nextPlans.some(plan =>
      plan.name.includes('starter_tier')
    )
  ) {
    return (
      <S.PrimaryCTAButton
        onClick={() => {
          window.analytics?.track('Add Tracked Hours Clicked', {
            location: 'primary plan card add tracked hours button',
            organizationName,
          });
          setActiveModal(ActiveModalType.UpgradeSelfServicePlanModal);
        }}>
        <S.IconAdd />
        Add tracked hours
      </S.PrimaryCTAButton>
    );
  }

  return undefined;
}
